/**
 * Monthly (SpotMe Now) base variant.
 *
 * Default for all `DealType.Monthly` listings. Primary CTA = Reservar
 * (`checkout` action). Lead form is disabled by default; the Monthly
 * surface uses the on-platform checkout flow instead.
 *
 * Note on Fleet: when a Monthly + Parking listing has multiple units
 * available, the PriceCard renders an ADDITIONAL fleet-trigger pill
 * (handled inside the component itself in Unit 3). That pill does NOT
 * replace the primary "Reservar" CTA, so the resolver returns the
 * Monthly variant unchanged for those listings. See
 * `./fleet.ts` for the variant the fleet pill drives when clicked
 * (consumed by `<LeadForm variant="fleet">` in Unit 8).
 */

import type { BaseListingVariant } from 'src/lib/listingVariants/types'

export const baseMonthlyVariant: BaseListingVariant = {
  priceCard: {
    primaryCta: {
      labelKey: 'CTA.RENT',
      action: 'checkout'
    },
    secondaryCta: null,
    showScarcity: true,
    scarcityCopyKey: 'SCARCITY.MONTHLY',
    showWatching: true,
    showStorageTimeChips: true,
    // Set at runtime by the resolver based on `configuration.availability`.
    showQuantityInput: false,
    showFleetCta: true,
    showPriceBreakdown: true,
    trustSignals: ['now.no_charge', 'now.response_time', 'now.free_cancel']
  },
  leadForm: {
    enabled: false,
    desktopContainer: 'inline-on-card',
    primaryChannel: 'email',
    steps: ['qualifier', 'contact', 'success'],
    fields: [],
    qualifierField: null,
    specialistKey: null,
    responseBadge: { show: false },
    whatsappRecipient: 'corporate',
    method: 'broker',
    leadType: 'demand',
    subType: null,
    postSubmit: 'thank-you',
    leadCopyKey: 'default'
  },
  sections: {
    // Anonymous, not named. Now is the only variant that ever showed the
    // landlord's name + logo + profile link to a renter, and it is the
    // variant where a bypass costs the most (a recurring 15-25% commission,
    // not a one-off lead fee). Broker/Fleet already swap in the specialist
    // card and Traditional already hides the section — this closes the last
    // hole. The renter learns who the host is at checkout, where the
    // contract requires it.
    user: { variant: 'anonymous' },
    size: { show: true, framing: 'b2c' },
    insurance: { show: true, mode: 'chubb-picker' },
    reviews: { show: true },
    refundBadge: { show: true },
    similarListings: { show: true, tier: 'same' },
    securityAndSupport: {
      headlineKey: 'SECURITY.MONTHLY.TITLE_PREFIX',
      accentKey: 'SECURITY.MONTHLY.TITLE_ACCENT',
      subtitleKey: 'SECURITY.MONTHLY.SUBTITLE',
      cardKeys: [
        'verification_monthly',
        'anti_fraud',
        'control_monthly',
        'support_monthly'
      ]
    }
  },
  checkout: {
    authGate: 'before-CTA-click',
    guestEmailOk: false
  }
}
